import {CommonModule} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {
  EnvironmentInjector,
  ErrorHandler,
  inject,
  NgModule,
  Optional,
  provideAppInitializer,
  runInInjectionContext,
  SkipSelf
} from '@angular/core';
import {Router} from '@angular/router';
import {provideTranslateService, TranslateLoader, TranslateModule} from '@ngx-translate/core';
import * as Sentry from "@sentry/angular";
import {Config, ConfigService} from './config';
import {LoaderModule} from './loader/loader.module';
import {HttpErrorInterceptor} from "./interceptors/http-error-interceptor.service";
import {GlobalErrorHandler} from "./handlers/globar-error.handler";
import {IntegrationService} from "./integration/integration.service";
import {MatomoInitializerService, provideMatomo, withRouteData, withRouter} from "ngx-matomo-client";
import packageJson from 'package.json'
import {firstValueFrom, map} from "rxjs";
import {ReferenceApiService} from "./api/reference-api/reference-api.service";
import {Utils} from "../shared/utils/generics.utils";
import {getSupportedLanguage, UiUtils} from "../shared/utils/ui.utils";
import {Company} from "./models/company";
import {ApiService} from "./api/api.service";
import {ExternalApiService} from "./api/external-api/external-api.service";
import {CustomTranslateHttpLoader} from "../shared/utils/custom-translate-http-loader";
import {ServiceInterceptor} from "./interceptors/service.interceptor";
import {Product} from "./models/product";
import 'moment/min/locales';
import {I18nService} from "../shared/services/i18n.service";
import {QueryParamBootstrapperService} from "./state/query-param-bootstrapper.service";
import {StorageService} from "./state/storage.service";
import {AppStateService} from "./state/app-state.service";
import {EventTrackerService} from "../shared/services/event-tracker.service";

const replacePrivateDataFromPath = (path: string) => {
  if (path?.includes('license=')) {
    return path.replace(/license=([^&]*)/, 'license=<LICENSE>');
  }

  if (path?.includes('postcode=')) {
    return path.replace(/postcode=([^&]*)/, 'postcode=<POSTCODE>');
  }

  if (path?.includes('email=')) {
    return path.replace(/email=([^&]*)/, 'email=<EMAIL>');
  }

  if (path?.includes('phone=')) {
    return path.replace(/phone=([^&]*)/, 'phone=<PHONE>');
  }
  return path;
}

const initMatomo = async (company: Company, referenceApi: ReferenceApiService, matomoInitializer: MatomoInitializerService, eventTrackerService: EventTrackerService) => {
  try {
    const matomoId = (await firstValueFrom(referenceApi.getMatomoId(company.id))).body?.matomoId;
    if (!matomoId) {
      const error = new Error('Unable to get matomo id');
      Sentry.captureException(error);
      throw error;
    }
    matomoInitializer.initializeTracker({
      siteId: matomoId,
      trackerUrl: getMatomoTrackerUrl()
    })
    eventTrackerService.setMatomoSiteId(matomoId);
  } catch (e: any) {
    matomoInitializer.initializeTracker({
      siteId: '1',
      trackerUrl: getMatomoTrackerUrl()
    })
    eventTrackerService.setMatomoSiteId(1);
  }
  eventTrackerService.setAppVersion(packageJson.version);
}

const initSentry = async (config: Config) => {
  Sentry.init({
    enabled: config.app.environment === 'prod' || config.webapp.enableSentry,
    environment: config.app.environment,
    dsn: 'https://00011b0ec7424dee86ef34ccedab4ec7@o1145449.ingest.sentry.io/6238717',
    integrations: (integrations) => {
      integrations.push(Sentry.browserTracingIntegration())
      return integrations;
    },
    release: packageJson.version,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 5,
    ignoreErrors: [
      '0 Unknown Error'
    ],
    // Important - GDPR - License is a personal data we don't want to pass to Sentry.
    // We exclude it from navigation path & http request.
    beforeBreadcrumb(breadcrumb) {
      // HTTP Request removal
      if (breadcrumb.category === 'xhr') {
        const url: string = breadcrumb?.data?.['url'];
        if (url.includes('licenses')) {
          if (breadcrumb && breadcrumb.data && breadcrumb.data['url']) {
            breadcrumb.data['url'] = url.replace(/licenses\/.*/, 'licenses/<LICENSE>');
          }

        }
      }

      // Navigation removal
      if (breadcrumb.category === 'navigation') {
        if (breadcrumb && breadcrumb.data && breadcrumb.data['from']) {
          breadcrumb.data['from'] = replacePrivateDataFromPath(breadcrumb.data['from']);
        }

        if (breadcrumb && breadcrumb.data && breadcrumb.data['to']) {
          breadcrumb.data['to'] = replacePrivateDataFromPath(breadcrumb.data['to']);
        }
      }
      return breadcrumb;
    },
  })
}

const getMatomoTrackerUrl = () => {
  return 'https://carool.matomo.cloud/';
}

const isDevelopment = () => {
  return ['localhost', '.dev.', '.val.'].some(t => window.location.host.includes(t));
}

const preconnectConfigurationS3 = () => {
  const host = window.location.host;
  let env = host.includes('localhost') ? 'dev' : host.split('.')[1];

  if (!['dev', 'val'].some(e => env === e)) {
    env = 'prod';
  }

  const link = document.createElement('link');
  link.href = `https://carool-configuration-${env}.s3.eu-west-1.amazonaws.com`;
  link.rel = 'preconnect';
  document.querySelector('head')?.appendChild(link)
}

@NgModule({
  declarations: [],
  exports: [
    TranslateModule,
    LoaderModule
  ], imports: [
    // Angular
    CommonModule,
    // CaRool
    LoaderModule
  ], providers: [
    ConfigService,
/*    provideAppInitializer(() => initializeApp(inject(ConfigService), inject(ReferenceApiService), inject(ApiService), inject(ExternalApiService), inject(MatomoInitializerService), inject(StoreService))),*/
    provideAppInitializer(async () => {

      const configService = inject(ConfigService);
      const bootstrap = inject(QueryParamBootstrapperService);
      const api = inject(ApiService);
      const referenceApi = inject(ReferenceApiService);
      const externalApi = inject(ExternalApiService);
      const storage = inject(StorageService);
      const matomoInitializer = inject(MatomoInitializerService);
      const eventTracker = inject(EventTrackerService);
      const injector = inject(EnvironmentInjector);

      // Init config first
      await configService.loadConfig();
      const config = configService.getConfig();

      // Init Sentry
      await initSentry(config);

      // Provide api with loaded config
      referenceApi.setConfig(config);
      externalApi.setConfig(config);
      api.setConfig(config);

      // Preconnect S3 urls for better performance
      preconnectConfigurationS3();

      // Get current company
      const company = await firstValueFrom(externalApi.getCompany(Utils.getCompanyFromDomain(), getSupportedLanguage()).pipe(map(dto => new Company(dto))));

      // Init matomo
      await initMatomo(company, referenceApi, matomoInitializer, eventTracker);

      // Read query params info
      const queryState = bootstrap.readParams();
      const product = queryState.product ?? Product.UNKNOWN;

      // Read info from storage
      const storageState = storage.toAppState(product);

      // Below service must be created after apis have been setup due to effects.
      const appState = injector.get(AppStateService);
      const languageService = injector.get(I18nService);

      appState.patch({company, language: queryState.lang ?? UiUtils.FALLBACK_LANG})
      await languageService.setLanguage(appState.snapshot.language ?? UiUtils.FALLBACK_LANG);
    }),
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService)
    }),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServiceInterceptor,
      multi: true
    },
    provideAppInitializer(() => {
      inject(IntegrationService)
    }),
    provideMatomo(
      {
        mode: "deferred",
        requireConsent: "cookie",
        disabled: isDevelopment()
      },
      withRouter(),
      withRouteData()
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateHttpLoader,
        deps: [HttpBackend, ConfigService]
      }
    })
  ]
})
export class CoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModule
  ) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in AppModule.');
    }
  }
}

